import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { RoboApiService } from './api.service';

export interface Employee {
  _id: string;
  email: string;
  name: string;
  role: 'admin' | 'operator' | 'viewer';
  allowed_accounts: string[];
}

export interface ClientAccount {
  _id: string;
  name: string;
  broker?: string;
  vps_ip: string;
  engines: { pair: string; port: number }[];
  notes?: string;
  active: boolean;
}

const TOKEN_KEY = 'robo_token';
const EMPLOYEE_KEY = 'robo_employee';
const ACCOUNT_KEY = 'robo_selected_account';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private api = inject(RoboApiService);
  private router = inject(Router);

  readonly token = signal<string | null>(this.loadToken());
  readonly employee = signal<Employee | null>(this.loadEmployee());
  readonly accounts = signal<ClientAccount[]>([]);
  readonly selectedAccount = signal<ClientAccount | null>(this.loadSelectedAccount());

  readonly isLoggedIn = computed(() => !!this.token() && !!this.employee());
  readonly isAdmin = computed(() => this.employee()?.role === 'admin');
  readonly selectedAccountId = computed(() => {
    const id = this.selectedAccount()?._id || null;
    if (!id) return null;
    // Account _id format is "acc-30111651" — extract the numeric MT5 account number
    // which is what signal_log, bar_decisions, heartbeats store in their `account` field.
    return id.includes('-') ? id.split('-').pop()! : id;
  });

  // ── Persistence ──

  private loadToken(): string | null {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token && this.isTokenExpired(token)) {
        console.warn('[Auth] Token expired on load — clearing');
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EMPLOYEE_KEY);
        return null;
      }
      return token;
    } catch {
      return null;
    }
  }

  /** HIGH-7: Decode JWT and check exp claim */
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return false;
      // Expired if less than 60s remaining
      return payload.exp * 1000 < Date.now() - 60000;
    } catch {
      return true; // malformed token → treat as expired
    }
  }

  private loadEmployee(): Employee | null {
    try {
      const raw = localStorage.getItem(EMPLOYEE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private loadSelectedAccount(): ClientAccount | null {
    try {
      const raw = localStorage.getItem(ACCOUNT_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed;
    } catch {
      return null;
    }
  }

  private persist() {
    try {
      const t = this.token();
      const e = this.employee();
      const a = this.selectedAccount();
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
      if (e) localStorage.setItem(EMPLOYEE_KEY, JSON.stringify(e));
      else localStorage.removeItem(EMPLOYEE_KEY);
      if (a) localStorage.setItem(ACCOUNT_KEY, JSON.stringify(a));
      else localStorage.removeItem(ACCOUNT_KEY);
    } catch {
      /* SSR or private mode */
    }
  }

  // ── Login / Logout ──

  async login(email: string, password: string): Promise<boolean> {
    try {
      const result = await this.api.login(email, password);
      this.token.set(result.token);
      this.employee.set(result.employee);
      this.persist();
      await this.loadAccounts();
      return true;
    } catch {
      return false;
    }
  }

  logout() {
    this.token.set(null);
    this.employee.set(null);
    this.accounts.set([]);
    this.selectedAccount.set(null);
    this.persist();
    this.router.navigate(['/login']);
  }

  // ── Accounts ──

  async loadAccounts() {
    try {
      // H4 FIX: Read token from localStorage as fallback to avoid signal timing issues
      const effectiveToken = this.token() || localStorage.getItem('robo_token');
      if (!effectiveToken) {
        console.warn('[Auth] loadAccounts: no token available');
        return;
      }
      const list = await this.api.getMyAccountsWithToken(effectiveToken);
      this.accounts.set(list);
      console.log(`[Auth] loadAccounts: ${list.length} accounts loaded`);
      const current = this.selectedAccount();
      const fresh = current ? list.find((a: any) => a._id === current._id) : null;
      if (fresh) {
        this.selectAccount(fresh);
      } else if (list.length > 0) {
        this.selectAccount(list[0]);
      }
    } catch (e: any) {
      console.error(`[Auth] loadAccounts failed (status=${e?.status || 'unknown'}):`, e?.message || e);
    }
  }

  selectAccount(account: ClientAccount | null) {
    this.selectedAccount.set(account);
    this.persist();
  }

  // ── Init (call from layout) ──

  async init() {
    // HIGH-7: Check token expiration on init and periodically
    const t = this.token();
    if (t && this.isTokenExpired(t)) {
      console.warn('[Auth] Token expired — auto-logout');
      this.logout();
      return;
    }
    if (this.isLoggedIn()) {
      await this.loadAccounts();
      // Check token expiration every 5 minutes
      setInterval(() => {
        const current = this.token();
        if (current && this.isTokenExpired(current)) {
          console.warn('[Auth] Token expired — auto-logout');
          this.logout();
        }
      }, 5 * 60 * 1000);
    }
  }
}
